import React, { useMemo, useEffect } from "react";
import { z } from "zod";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateRouteAccessFromCookie } from "@/utils/auth-middleware";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ComboboxDemo } from "@/components/ui/combobox";
import {
  PrivateOfficeStatus,
  PrivateOfficeLifecycleState,
  LIFECYCLE_STATE_LABELS,
  isTransientLifecycleState,
  NUMERIC_TO_STRING_STATUS_MAP,
  MANUAL_LIFECYCLE_STATE_OPTIONS,
  MANUAL_LIFECYCLE_STATE_OPTIONS_INTERNAL_USER,
} from "@/@types/private-office.types";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { useSpaceSelection } from "@/hooks/useSpaceSelection";
import { debounce } from "@/utils/debounce";
import { ChevronDown, ChevronUp } from "lucide-react";
import { StepProps } from "@/components/multi-step-form";
import { generatePrivateRoomUrl } from "@/features/private-room/list/utils/generate-private-room-url/generate-private-room-url";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useToast } from "@/components/ui/use-toast";
import { privateOfficeService } from "@/services/private-office-service";
import { spaceService } from "@/services/space-service";
import { useStatisticalAnalysis } from "@/hooks/useStatisticalAnalysis";
import {
  NeighborhoodMissingAlert,
  PriceAnalysisAlert,
  SizeCapacityAlert,
} from "@/components/statistical-alerts";

export const dadosPrincipaisSchema = z.object({
  externalSpaceId: z
    .string({ required_error: 'O campo "Espaço" é obrigatório' })
    .min(1, 'O campo "Espaço" é obrigatório'),
  spaceName: z.string().optional(),
  spaceRooms: z.array(z.any()).optional(),
  name: z
    .string({ required_error: 'O campo "Nome" é obrigatório' })
    .min(1, 'O campo "Nome" é obrigatório')
    .max(50, "O nome da sala deve ter no máximo 50 caracteres"),
  size: z
    .string({ required_error: "O tamanho deve ser maior que 5 m²" })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const num = parseFloat(val);
      return !isNaN(num) && num > 5;
    }, "O tamanho deve ser maior que 5 m²"),
  capacity: z
    .string({ required_error: "A capacidade deve ser no mínimo 1 pessoa" })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const num = parseInt(val);
      return !isNaN(num) && num >= 1;
    }, "A capacidade deve ser no mínimo 1 pessoa"),
  status: z
    .string({ required_error: 'O campo "Status" é obrigatório' })
    .min(1, 'O campo "Status" é obrigatório'),
  monthlyPrice: z
    .string({
      required_error: "O preço mensal deve ser igual ou maior que R$ 500",
    })
    .refine((val) => {
      if (!val || val.trim() === "") return false;
      const cleanedValue = val.replace(/\./g, "").replace(",", ".");
      const num = parseFloat(cleanedValue);
      return !isNaN(num) && num >= 500;
    }, "O preço mensal deve ser igual ou maior que R$ 500"),
  description: z.string().optional(),
  notes: z.string().optional(),
  daysOfOperation: z
    .enum(["monday-friday", "monday-saturday", "custom"])
    .optional(),
  operatingHours: z
    .object({
      sunday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      monday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      tuesday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      wednesday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      thursday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      friday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
      saturday: z
        .array(
          z.object({
            open: z.string(),
            close: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  timezone: z.string().optional(),
  clonedAmenitiesData: z.any().optional(),
  lifecycleState: z.nativeEnum(PrivateOfficeLifecycleState).optional(),
  availabilityDate: z.string().optional(),
});

export const dadosPrincipaisSchemaWithRefinement = dadosPrincipaisSchema
  .refine(
    (data) => {
      if (
        data.status === PrivateOfficeStatus.UNAVAILABLE &&
        data.lifecycleState &&
        isTransientLifecycleState(data.lifecycleState)
      ) {
        return !!data.availabilityDate;
      }
      return true;
    },
    { message: "Informe a data de retorno", path: ["availabilityDate"] },
  )
  .refine(
    (data) => {
      if (!data.availabilityDate) return true;
      const today = new Date().toISOString().split("T")[0];
      return data.availabilityDate >= today;
    },
    {
      message: "A data de retorno não pode ser anterior à data atual",
      path: ["availabilityDate"],
    },
  );

export type DadosPrincipaisData = z.infer<typeof dadosPrincipaisSchema>;

const todayIso = new Date().toISOString().split("T")[0];

export const StepDadosPrincipais: React.FC<StepProps> = ({
  errors,
  registerField,
}) => {
  const { register, watch, setValue } = useFormContext();
  const watchedValues = watch();
  const [isCollapsibleOpen, setIsCollapsibleOpen] = React.useState(false);
  const { pathname } = useLocation();
  const { toast } = useToast();
  const { isInternalUser } = useAuthGuard();

  // Determine user type
  const isExternalRoute = pathname.includes("/external/");

  // Function to extract spaceId from cookie
  const extractSpaceIdFromCookie = (): string | null => {
    try {
      const cookies = document.cookie.split(";");
      const spaceIdCookie = cookies.find((cookie) =>
        cookie.trim().startsWith("space_id="),
      );
      return spaceIdCookie ? spaceIdCookie.split("=")[1] : null;
    } catch (error) {
      console.error("Erro ao extrair space_id do cookie:", error);
      return null;
    }
  };

  // Days of the week mapping
  const daysOfWeek = [
    { key: "sunday", label: "Domingo" },
    { key: "monday", label: "Segunda-feira" },
    { key: "tuesday", label: "Terça-feira" },
    { key: "wednesday", label: "Quarta-feira" },
    { key: "thursday", label: "Quinta-feira" },
    { key: "friday", label: "Sexta-feira" },
    { key: "saturday", label: "Sábado" },
  ];

  const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"];
  const SATURDAY_ONLY = ["saturday"];

  // Initialize operating hours and timezone
  useEffect(() => {
    if (!watchedValues.timezone) {
      setValue("timezone", "America/Sao_Paulo");
    }
    if (!watchedValues.daysOfOperation) {
      setValue("daysOfOperation", "monday-friday");
    }
    if (!watchedValues.operatingHours) {
      // Initialize with default monday-friday schedule
      setValue("operatingHours", {
        sunday: [],
        monday: [{ open: "08:00", close: "18:00" }],
        tuesday: [{ open: "08:00", close: "18:00" }],
        wednesday: [{ open: "08:00", close: "18:00" }],
        thursday: [{ open: "08:00", close: "18:00" }],
        friday: [{ open: "08:00", close: "18:00" }],
        saturday: [],
      });
    }
  }, [setValue]);

  const { spaces, spacesPO, handleSearchSpaces, handleSpaceSelect } =
    useSpaceSelection({});

  // Auto-select space from cookie for external routes (with proper initialization check)
  useEffect(() => {
    // Only proceed if this is an external route and no space is selected yet
    if (!isExternalRoute || watchedValues.externalSpaceId) {
      return;
    }

    // Ensure hooks are properly initialized before proceeding
    if (!spaces || !handleSpaceSelect) {
      return;
    }

    const cookieSpaceId = extractSpaceIdFromCookie();

    if (!cookieSpaceId) {
      console.warn("⚠️ No space_id found in cookie for external route");
      toast({
        variant: "destructive",
        title: "Erro de Configuração",
        description:
          "Usuários externos devem ter um espaço pré-configurado. Cookie space_id não encontrado. Entre em contato com o suporte.",
      });
      return;
    }

    // Validate if user has access to this space
    try {
      const authResult = validateRouteAccessFromCookie(true); // External route

      if (!authResult.hasAccess) {
        console.error(
          "❌ User doesn't have access to space from cookie:",
          cookieSpaceId,
        );
        toast({
          variant: "destructive",
          title: "Acesso Negado",
          description:
            "Você não tem acesso ao espaço configurado. Selecione um espaço disponível.",
        });
        return;
      }

      // Auto-select the space
      setValue("externalSpaceId", cookieSpaceId);

      // Find space name from spaces list (may not be available yet)
      const selectedSpace = spaces.find(
        (space) => space.value === cookieSpaceId,
      );

      // Function to fetch space name from API as fallback
      const fetchSpaceName = async () => {
        try {
          const spaceData = await spaceService.getSpaceById({
            spaceId: cookieSpaceId,
          });
          setValue("spaceName", spaceData.name);
          return spaceData.name;
        } catch (error) {
          console.error("❌ Failed to fetch space name from API:", error);
          return null;
        }
      };

      // Set space name immediately if available in spaces list
      if (selectedSpace) {
        setValue("spaceName", selectedSpace.label);
      } else {
        // Fetch space name from API as fallback
        fetchSpaceName();
      }

      // Fetch space details including neighborhood and rooms
      Promise.all([
        spaceService.getSpaceById({ spaceId: cookieSpaceId }),
        handleSpaceSelect({ spaceId: cookieSpaceId }),
      ])
        .then(([space]) => {
          // Set neighborhood for statistical analysis
          setNeighborhood(space.neighborhood);

          // Double-check that spaceName is set after API call
          const currentFormData = watch();
          if (!currentFormData.spaceName) {
            if (selectedSpace) {
              setValue("spaceName", selectedSpace.label);
            } else {
              // If still no space name, try to fetch it
              fetchSpaceName();
            }
          }

          toast({
            title: "Espaço selecionado automaticamente",
            description: `Espaço ${currentFormData.spaceName || cookieSpaceId} foi selecionado baseado nas suas permissões`,
          });
        })
        .catch((error) => {
          console.error("❌ Error fetching space details:", error);

          // Ensure space name is set even if handleSpaceSelect fails
          if (selectedSpace && !watch().spaceName) {
            setValue("spaceName", selectedSpace.label);
          } else if (!selectedSpace && !watch().spaceName) {
            fetchSpaceName();
          }
        })
        .finally(() => {
          setNeighborhoodFetched(true);
        });
    } catch (error) {
      console.error("❌ Error validating space access:", error);
      toast({
        variant: "destructive",
        title: "Erro de Validação",
        description:
          "Erro ao validar acesso ao espaço. Selecione um espaço manualmente.",
      });
    }
  }, [
    isExternalRoute,
    watchedValues.externalSpaceId,
    setValue,
    spaces,
    handleSpaceSelect,
    toast,
    watch,
  ]);

  // Sync operating hours when daysOfOperation changes (handles cases where the select doesn't trigger handleDaysOfOperationChange)
  useEffect(() => {
    if (watchedValues.daysOfOperation && watchedValues.operatingHours) {
      const currentDaysOfOperation = watchedValues.daysOfOperation;
      const currentOperatingHours = watchedValues.operatingHours;

      // Check if current hours match the selected preset
      const defaultHours = { open: "08:00", close: "18:00" };
      const saturdayHours = { open: "08:00", close: "14:00" };

      let expectedHours;
      switch (currentDaysOfOperation) {
        case "monday-friday":
          expectedHours = {
            sunday: [],
            monday: [{ ...defaultHours }],
            tuesday: [{ ...defaultHours }],
            wednesday: [{ ...defaultHours }],
            thursday: [{ ...defaultHours }],
            friday: [{ ...defaultHours }],
            saturday: [],
          };
          break;
        case "monday-saturday":
          expectedHours = {
            sunday: [],
            monday: [{ ...defaultHours }],
            tuesday: [{ ...defaultHours }],
            wednesday: [{ ...defaultHours }],
            thursday: [{ ...defaultHours }],
            friday: [{ ...defaultHours }],
            saturday: [{ ...saturdayHours }],
          };
          break;
        default:
          expectedHours = null;
      }

      // Only update if we have expected hours and current hours don't match
      if (expectedHours) {
        const needsUpdate = Object.keys(expectedHours).some((day) => {
          const currentDayHours = currentOperatingHours[day] || [];
          const expectedDayHours = expectedHours[day] || [];

          if (currentDayHours.length !== expectedDayHours.length) return true;
          if (currentDayHours.length === 0) return false;

          return (
            currentDayHours[0]?.open !== expectedDayHours[0]?.open ||
            currentDayHours[0]?.close !== expectedDayHours[0]?.close
          );
        });

        if (needsUpdate) {
          setValue("operatingHours", expectedHours);
        }
      }
    }
  }, [watchedValues.daysOfOperation, setValue]);

  // Handle days of operation selection
  const handleDaysOfOperationChange = (value: string) => {
    setValue("daysOfOperation", value);

    const defaultHours = { open: "08:00", close: "18:00" };
    const saturdayHours = { open: "08:00", close: "14:00" };

    let newOperatingHours;

    switch (value) {
      case "monday-friday":
        newOperatingHours = {
          sunday: [],
          monday: [{ ...defaultHours }],
          tuesday: [{ ...defaultHours }],
          wednesday: [{ ...defaultHours }],
          thursday: [{ ...defaultHours }],
          friday: [{ ...defaultHours }],
          saturday: [],
        };
        break;
      case "monday-saturday":
        newOperatingHours = {
          sunday: [],
          monday: [{ ...defaultHours }],
          tuesday: [{ ...defaultHours }],
          wednesday: [{ ...defaultHours }],
          thursday: [{ ...defaultHours }],
          friday: [{ ...defaultHours }],
          saturday: [{ ...saturdayHours }],
        };
        break;
      case "custom": {
        // Keep current hours or initialize with empty arrays
        const currentHours = watchedValues.operatingHours || {};
        newOperatingHours = {
          sunday: currentHours.sunday || [],
          monday: currentHours.monday || [],
          tuesday: currentHours.tuesday || [],
          wednesday: currentHours.wednesday || [],
          thursday: currentHours.thursday || [],
          friday: currentHours.friday || [],
          saturday: currentHours.saturday || [],
        };
        break;
      }
      default:
        newOperatingHours = {
          sunday: [],
          monday: [],
          tuesday: [],
          wednesday: [],
          thursday: [],
          friday: [],
          saturday: [],
        };
    }

    setValue("operatingHours", newOperatingHours);
  };

  // Handle operating hours changes
  const handleDayToggle = (dayKey: string, enabled: boolean) => {
    const currentOperatingHours = watchedValues.operatingHours || {};
    const currentDayHours = currentOperatingHours[dayKey] || [];

    if (enabled && currentDayHours.length === 0) {
      // Add default time slot when enabling
      setValue("operatingHours", {
        ...currentOperatingHours,
        [dayKey]: [{ open: "08:00", close: "18:00" }],
      });
    } else if (!enabled) {
      // Remove all time slots when disabling
      setValue("operatingHours", {
        ...currentOperatingHours,
        [dayKey]: [],
      });
    }
  };

  const getCurrentTimeValue = (
    timeType: "open" | "close",
    daysToCheck: string[],
    defaultValue: string,
  ) => {
    const operatingHours = watchedValues.operatingHours || {};
    for (const day of daysToCheck) {
      if (operatingHours[day]?.[0]?.[timeType]) {
        return operatingHours[day][0][timeType];
      }
    }
    return defaultValue;
  };

  const TimeSelect = ({
    timeType,
    daysToCheck,
    defaultValue,
    onValueChange,
    disabled = false,
  }: {
    timeType: "open" | "close";
    daysToCheck: string[];
    defaultValue: string;
    onValueChange: (value: string) => void;
    disabled?: boolean;
  }) => (
    <Select
      value={getCurrentTimeValue(timeType, daysToCheck, defaultValue)}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-24">
        <SelectValue placeholder={defaultValue} />
      </SelectTrigger>
      <SelectContent>
        {Array.from({ length: 24 }, (_, i) => {
          const hour = i.toString().padStart(2, "0");
          return (
            <SelectItem key={hour} value={`${hour}:00`}>
              {hour}:00
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );

  const handleTimeChange = (
    dayKey: string,
    timeType: "open" | "close",
    value: string,
  ) => {
    const currentOperatingHours = watchedValues.operatingHours || {};
    const daysOfOperation = watchedValues.daysOfOperation || "monday-friday";

    let daysToUpdate: string[] = [];

    if (daysOfOperation === "monday-friday") {
      daysToUpdate = [...WEEKDAYS];
    } else if (daysOfOperation === "monday-saturday") {
      if (dayKey === "saturday") {
        daysToUpdate = [...SATURDAY_ONLY];
      } else {
        daysToUpdate = [...WEEKDAYS];
      }
    } else if (daysOfOperation === "custom") {
      daysToUpdate = [dayKey];
    }

    const updatedOperatingHours = { ...currentOperatingHours };

    daysToUpdate.forEach((day) => {
      const currentDayHours = updatedOperatingHours[day] || [];

      const defaultClose = day === "saturday" ? "14:00" : "18:00";
      const updatedDayHours =
        currentDayHours.length > 0
          ? [...currentDayHours]
          : [{ open: "08:00", close: defaultClose }];

      updatedDayHours[0] = {
        ...updatedDayHours[0],
        [timeType]: value,
      };

      updatedOperatingHours[day] = updatedDayHours;
    });

    if (daysOfOperation !== "custom") {
      const allDays = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      allDays.forEach((day) => {
        if (
          daysOfOperation === "monday-friday" &&
          !WEEKDAYS.includes(day as any)
        ) {
          updatedOperatingHours[day] = [];
        } else if (daysOfOperation === "monday-saturday" && day === "sunday") {
          updatedOperatingHours[day] = [];
        }
      });
    }

    setValue("operatingHours", updatedOperatingHours, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const formatCurrency = (value: string) => {
    const cleanedValue = value.replace(/\D/g, "");

    if (!cleanedValue) return "";

    const numberValue = parseFloat(cleanedValue) / 100;
    return numberValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const validateName = (name: string): string | null => {
    if (!name || name.trim() === "") {
      return "O campo Nome é obrigatório";
    }
    if (name.length > 50) {
      return "O nome da sala deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const validatePrice = (price: string): string | null => {
    if (!price || price.trim() === "") {
      return "O campo Preço mensal é obrigatório";
    }
    const cleanedValue = price.replace(/\./g, "").replace(",", ".");
    const num = parseFloat(cleanedValue);
    if (isNaN(num) || num < 500) {
      return "O preço mensal deve ser igual ou maior que R$ 500";
    }
    return null;
  };

  const [nameError, setNameError] = React.useState<string | null>(null);
  const [priceError, setPriceError] = React.useState<string | null>(null);
  const [neighborhood, setNeighborhood] = React.useState<string | null>(null);
  const [neighborhoodFetched, setNeighborhoodFetched] =
    React.useState<boolean>(false);

  // Statistical analysis hook - only enable when we have the required data
  const {
    data: statisticalData,
    isLoading: isLoadingAnalysis,
    hasRequiredData,
    hasNeighborhood,
    localSizeCapacityRatio,
  } = useStatisticalAnalysis({
    neighborhood,
    monthlyPrice: watchedValues.monthlyPrice,
    capacity: watchedValues.capacity,
    sizeM2: watchedValues.size,
    enabled: Boolean(watchedValues.capacity && watchedValues.size),
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue("name", value);
    setNameError(validateName(value));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatCurrency(inputValue);
    setValue("monthlyPrice", formattedValue);
    setPriceError(validatePrice(formattedValue));
  };

  const handleSpaceSelectAndExpand = async (spaceId: string) => {
    setValue("externalSpaceId", spaceId);
    const selectedSpace = spaces.find((space) => space.value === spaceId);
    setValue("spaceName", selectedSpace?.label || "");
    setNeighborhood(null);
    setNeighborhoodFetched(false);

    // Fetch space details including neighborhood
    try {
      const space = await spaceService.getSpaceById({ spaceId });
      setNeighborhood(space.neighborhood);
    } catch (error) {
      console.error("Failed to fetch space neighborhood:", error);
    } finally {
      setNeighborhoodFetched(true);
    }

    await handleSpaceSelect({ spaceId });
  };

  // Use the stored space name or fallback to finding it in the array
  const selectedSpaceLabel = useMemo(() => {
    if (!watchedValues.externalSpaceId) return "";
    if (watchedValues.spaceName) return watchedValues.spaceName;
    const selectedSpace = spaces.find(
      (space) => space.value === watchedValues.externalSpaceId,
    );
    return selectedSpace?.label || watchedValues.externalSpaceId; // Fallback to externalSpaceId if no label found
  }, [watchedValues.externalSpaceId, watchedValues.spaceName, spaces]);

  // Store rooms in form when fetched
  useEffect(() => {
    if (spacesPO.length > 0) {
      setValue("spaceRooms", spacesPO);
      setIsCollapsibleOpen(true);
    }
  }, [spacesPO, setValue]);

  // Auto-expand if we already have stored rooms when returning to this step
  useEffect(() => {
    if (watchedValues.spaceRooms && watchedValues.spaceRooms.length > 0) {
      setIsCollapsibleOpen(true);
    }
  }, [watchedValues.spaceRooms]);

  // Auto-fetch space name and rooms when externalSpaceId exists but spaceName doesn't (from API)
  useEffect(() => {
    if (
      watchedValues.externalSpaceId &&
      !watchedValues.spaceName &&
      !selectedSpaceLabel.includes("Teste")
    ) {
      // Only fetch if we have externalSpaceId but no spaceName (coming from API)
      handleSpaceSelect({ spaceId: watchedValues.externalSpaceId });
    }
  }, [
    watchedValues.externalSpaceId,
    watchedValues.spaceName,
    selectedSpaceLabel,
    handleSpaceSelect,
  ]);

  useEffect(() => {
    const fetchNeighborhoodForExistingSpace = async () => {
      if (
        watchedValues.externalSpaceId &&
        !neighborhood &&
        !neighborhoodFetched
      ) {
        try {
          const space = await spaceService.getSpaceById({
            spaceId: watchedValues.externalSpaceId,
          });
          setNeighborhood(space.neighborhood);
        } catch (error) {
          console.error(
            "❌ Failed to fetch neighborhood for existing space:",
            error,
          );
        } finally {
          setNeighborhoodFetched(true);
        }
      }
    };

    fetchNeighborhoodForExistingSpace();
  }, [watchedValues.externalSpaceId, neighborhood, neighborhoodFetched]);

  const handleCloneRoom = async (po: any) => {
    try {
      // Show loading toast
      toast({
        title: "Clonando sala...",
        description: "Copiando dados e amenidades da sala selecionada.",
      });

      // Set basic room data
      setValue("name", po.label);
      setValue("size", po.size?.toString());
      setValue("capacity", po.capacity.toString());
      // Convert numeric status to string status using the mapping
      const stringStatus =
        NUMERIC_TO_STRING_STATUS_MAP[
          po.status as keyof typeof NUMERIC_TO_STRING_STATUS_MAP
        ] || PrivateOfficeStatus.AVAILABLE;
      setValue("status", stringStatus);
      setValue(
        "monthlyPrice",
        po.monthlyPrice.toLocaleString("pt-BR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      );
      setValue("notes", po.note || "");

      // Fetch and store amenities data for the cloned room
      const amenitiesData = await privateOfficeService.getAmenitiesForCloning(
        po.id,
      );
      if (amenitiesData) {
        setValue("clonedAmenitiesData", amenitiesData);
      } else {
        console.warn("Failed to fetch amenities for cloning");
      }

      // Close the collapsible section
      setIsCollapsibleOpen(false);

      // Focus on the first input field after a short delay
      setTimeout(() => {
        const nameInput = document.getElementById("name");
        if (nameInput) {
          nameInput.focus();
          nameInput.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);

      toast({
        title: "Clonagem concluída com sucesso!",
        description: `A sala ${po.label} (ID ${po.search_id}) foi copiada com suas amenidades e já está pronta para edição.`,
      });
    } catch (error) {
      console.error("Error cloning room:", error);
      toast({
        title: "Erro na clonagem",
        description: "Ocorreu um erro ao clonar a sala. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleVisualizeRoom = (po: any) => {
    const roomUrl = generatePrivateRoomUrl(po.id.toString(), "80");

    sendTrackDataServer({
      url: pathname,
      eventName: "VIEW_PAGE_CLICKED",
      productType: "PO",
      data: {
        room_id: po.id,
      },
    });

    window.open(roomUrl, "_blank", "noopener,noreferrer");
  };

  // Disable fields when no space is selected (for create flow)
  const fieldsDisabled = !watchedValues.externalSpaceId;

  // For external routes, disable space selection (it's auto-selected from cookie)
  // For internal routes, always allow space selection
  const spaceSelectionDisabled = isExternalRoute;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">
          Vincular sala a um espaço
        </h3>
        <div className="space-y-4">
          <div
            className="flex flex-col gap-2"
            ref={
              errors.externalSpaceId && registerField
                ? (el) => registerField("externalSpaceId", el)
                : undefined
            }
          >
            <Label htmlFor="externalSpaceId">Espaço*</Label>

            {/* Show error message for external users without cookie */}
            {isExternalRoute && !watchedValues.externalSpaceId && (
              <div className="mb-2 p-3 border border-red-200 bg-red-50 rounded-md">
                <span className="text-sm text-red-700">
                  ❌ Espaço não configurado para usuário externo
                </span>
              </div>
            )}

            <ComboboxDemo
              key="space-selector"
              defaultValue={watchedValues.externalSpaceId || ""}
              emptyPlaceholder="Espaço não encontrado"
              searchPlaceholder="Busque um espaço"
              selectPlaceholder={
                isExternalRoute
                  ? "Espaço será carregado automaticamente"
                  : "Busque ou selecione um espaço"
              }
              widthContent="w-full"
              onChange={debounce(handleSearchSpaces, 500)}
              items={(() => {
                // If we have a externalSpaceId and spaceName but the space is not in the current list,
                // add it temporarily so the combobox can display the name
                if (
                  watchedValues.externalSpaceId &&
                  watchedValues.spaceName &&
                  !spaces.find((s) => s.value === watchedValues.externalSpaceId)
                ) {
                  return [
                    {
                      value: watchedValues.externalSpaceId,
                      label: watchedValues.spaceName,
                    },
                    ...spaces,
                  ];
                }
                return spaces;
              })()}
              onSelect={handleSpaceSelectAndExpand}
              shouldFilter={false}
              disabled={spaceSelectionDisabled}
            />

            {/* Show status indicator */}
            {watchedValues.externalSpaceId && (
              <div className="mt-2">
                <span
                  className={`text-xs px-2 py-1 rounded italic ${
                    isExternalRoute
                      ? "bg-green-100 text-green-700"
                      : "bg-[#F1F5F9] text-[#64748B]"
                  }`}
                >
                  {isExternalRoute ? "Auto-selecionado" : "Espaço vinculado"}
                </span>
              </div>
            )}
            {errors.externalSpaceId && (
              <p className="text-sm text-destructive mt-1">
                {errors.externalSpaceId[0]}
              </p>
            )}
          </div>

          {watchedValues.spaceRooms && watchedValues.spaceRooms.length > 0 && (
            <div>
              <Button
                variant="ghost"
                onClick={() => setIsCollapsibleOpen(!isCollapsibleOpen)}
                className="flex items-center justify-between w-full p-0 h-auto font-normal text-left hover:bg-transparent"
              >
                <span className="text-base">
                  Salas já cadastradas nesse espaço
                </span>
                {isCollapsibleOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>

              {isCollapsibleOpen && (
                <div className="mt-4 space-y-3 max-h-[400px] overflow-y-auto">
                  {watchedValues.spaceRooms.map((po: any) => (
                    <div
                      key={po.id}
                      className="flex items-center justify-between w-full p-2 rounded-lg"
                      style={{
                        backgroundColor: "#FCFBF9",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="flex-1 text-gray-700 text-sm">
                        <span>
                          ID {po.search_id} | Capacidade {po.capacity} pessoas |
                          Preço R${" "}
                          {po.monthlyPrice.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div className="flex gap-4 ml-4">
                        <Button
                          variant="outline"
                          onClick={() => handleVisualizeRoom(po)}
                          className="text-gray-600 hover:text-gray-900 px-4 py-2 font-normal"
                        >
                          Visualizar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleCloneRoom(po)}
                          className="text-gray-600 hover:text-gray-900 px-4 py-2 font-normal"
                        >
                          Clonar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={`bg-white rounded-lg border p-6 ${fieldsDisabled ? "opacity-50" : ""}`}
      >
        <h3 className="text-lg font-semibold mb-4">Informações da sala</h3>
        <div className="space-y-4">
          <div
            ref={
              errors.name && registerField
                ? (el) => registerField("name", el)
                : undefined
            }
          >
            <Label htmlFor="name">Nome*</Label>
            <Input
              id="name"
              placeholder="Ex: Sala Rio 01, Privativa Alpha"
              value={watchedValues.name || ""}
              onChange={handleNameChange}
              disabled={fieldsDisabled}
            />
            {(nameError || errors.name) && (
              <p className="text-sm text-destructive mt-1">
                {nameError || errors.name?.[0]}
              </p>
            )}
          </div>

          <div
            ref={
              errors.description && registerField
                ? (el) => registerField("description", el)
                : undefined
            }
          >
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              rows={4}
              value={watchedValues.description || ""}
              onChange={(e) => setValue("description", e.target.value)}
              className="mt-2"
              disabled={fieldsDisabled}
            />
            <div className="flex justify-between items-center mt-1">
              <div>
                {errors.description && (
                  <p className="text-sm text-destructive">
                    {errors.description[0]}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {(watchedValues.description || "").length} caracteres
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              ref={
                errors.size && registerField
                  ? (el) => registerField("size", el)
                  : undefined
              }
            >
              <Label htmlFor="size">Tamanho em m²*</Label>
              <Input
                id="size"
                type="number"
                min="0"
                step="0.1"
                placeholder="35"
                {...register("size")}
                disabled={fieldsDisabled}
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                }}
              />
              {errors.size && (
                <p className="text-sm text-destructive mt-1">
                  {errors.size[0]}
                </p>
              )}
            </div>

            <div
              ref={
                errors.capacity && registerField
                  ? (el) => registerField("capacity", el)
                  : undefined
              }
            >
              <Label htmlFor="capacity">Capacidade*</Label>
              <Input
                id="capacity"
                type="number"
                min="0"
                step="1"
                placeholder="8"
                {...register("capacity")}
                disabled={fieldsDisabled}
                onKeyDown={(e) => {
                  if (
                    e.key === "-" ||
                    e.key === "e" ||
                    e.key === "E" ||
                    e.key === "."
                  ) {
                    e.preventDefault();
                  }
                }}
              />
              {errors.capacity && (
                <p className="text-sm text-destructive mt-1">
                  {errors.capacity[0]}
                </p>
              )}
            </div>
          </div>

          {watchedValues.externalSpaceId &&
          neighborhoodFetched &&
          !hasNeighborhood ? (
            <NeighborhoodMissingAlert
              spaceId={watchedValues.externalSpaceId}
              spaceName={selectedSpaceLabel}
              screen="private-office-create"
            />
          ) : (
            <SizeCapacityAlert
              spaceRatioValidation={statisticalData?.spaceRatioValidation}
              localSizeCapacityRatio={localSizeCapacityRatio}
              loading={isLoadingAnalysis}
            />
          )}

          <div
            ref={
              errors.status && registerField
                ? (el) => registerField("status", el)
                : undefined
            }
          >
            <Label htmlFor="status">Status*</Label>
            <Select
              value={watchedValues.status || ""}
              onValueChange={(value) => {
                setValue("status", value);
                if (value !== PrivateOfficeStatus.UNAVAILABLE) {
                  setValue("lifecycleState", undefined);
                  setValue("availabilityDate", undefined);
                }
              }}
              disabled={fieldsDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PrivateOfficeStatus.AVAILABLE}>
                  Disponível
                </SelectItem>
                <SelectItem value={PrivateOfficeStatus.UNAVAILABLE}>
                  Indisponível
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.status && (
              <p className="text-sm text-destructive mt-1">
                {errors.status[0]}
              </p>
            )}
          </div>

          {watchedValues.status === PrivateOfficeStatus.UNAVAILABLE && (
            <>
              <div
                ref={
                  errors.lifecycleState && registerField
                    ? (el) => registerField("lifecycleState", el)
                    : undefined
                }
              >
                <Label htmlFor="lifecycleState">
                  Motivo de indisponibilidade*
                </Label>
                <Select
                  value={watchedValues.lifecycleState || ""}
                  onValueChange={(value) => {
                    const state = value as PrivateOfficeLifecycleState;
                    setValue("lifecycleState", state);
                    if (!isTransientLifecycleState(state)) {
                      setValue("availabilityDate", undefined);
                    }
                  }}
                  disabled={fieldsDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {(isInternalUser
                      ? MANUAL_LIFECYCLE_STATE_OPTIONS_INTERNAL_USER
                      : MANUAL_LIFECYCLE_STATE_OPTIONS
                    ).map((v) => (
                      <SelectItem key={v} value={v}>
                        {LIFECYCLE_STATE_LABELS[v]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.lifecycleState && (
                  <p className="text-sm text-destructive mt-1">
                    {errors.lifecycleState?.[0]}
                  </p>
                )}
              </div>

              {watchedValues.lifecycleState &&
                isTransientLifecycleState(
                  watchedValues.lifecycleState as PrivateOfficeLifecycleState,
                ) && (
                  <div
                    ref={
                      errors.availabilityDate && registerField
                        ? (el) => registerField("availabilityDate", el)
                        : undefined
                    }
                  >
                    <Label htmlFor="availabilityDate">Disponível em*</Label>
                    <Input
                      id="availabilityDate"
                      type="date"
                      min={todayIso}
                      value={watchedValues.availabilityDate || ""}
                      onChange={(e) => {
                        if (
                          e.target.value >= todayIso ||
                          e.target.value === ""
                        ) {
                          setValue("availabilityDate", e.target.value);
                        }
                      }}
                      disabled={fieldsDisabled}
                      className="mt-1"
                    />
                    {errors.availabilityDate && (
                      <p className="text-sm text-destructive mt-1">
                        {errors.availabilityDate?.[0]}
                      </p>
                    )}
                  </div>
                )}
            </>
          )}

          <div>
            <Label htmlFor="notes">Notas</Label>
            <Textarea
              id="notes"
              rows={4}
              {...register("notes")}
              className="mt-2"
              disabled={fieldsDisabled}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Apenas visível para o time interno
            </p>
            {errors.notes && (
              <p className="text-sm text-destructive mt-1">{errors.notes[0]}</p>
            )}
          </div>
        </div>
      </div>

      <div
        className={`bg-white rounded-lg border p-6 ${fieldsDisabled ? "opacity-50" : ""}`}
      >
        <h3 className="text-lg font-semibold mb-4">Horário de funcionamento</h3>

        <div className="space-y-6">
          <div>
            <h4 className="text-base font-medium mb-4">
              Dias de funcionamento
            </h4>
            <Select
              value={watchedValues.daysOfOperation || "monday-friday"}
              onValueChange={handleDaysOfOperationChange}
              disabled={fieldsDisabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monday-friday">Segunda à sexta</SelectItem>
                <SelectItem value="monday-saturday">
                  Segunda à sábado
                </SelectItem>
                <SelectItem value="custom">Personalizado por dia</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-2">
              Escolha em quais dias da semana a sala estará disponível
            </p>
          </div>

          {watchedValues.daysOfOperation === "monday-friday" && (
            <div>
              <h4 className="text-base font-medium mb-4">
                Horário de Funcionamento (Segunda a sexta)
              </h4>
              <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                <TimeSelect
                  timeType="open"
                  daysToCheck={WEEKDAYS}
                  defaultValue="08:00"
                  onValueChange={(value) => {
                    WEEKDAYS.forEach((day) => {
                      handleTimeChange(day, "open", value);
                    });
                  }}
                  disabled={fieldsDisabled}
                />
                <span className="text-sm text-muted-foreground">às</span>
                <TimeSelect
                  timeType="close"
                  daysToCheck={WEEKDAYS}
                  defaultValue="18:00"
                  onValueChange={(value) => {
                    WEEKDAYS.forEach((day) => {
                      handleTimeChange(day, "close", value);
                    });
                  }}
                  disabled={fieldsDisabled}
                />
              </div>
            </div>
          )}

          {watchedValues.daysOfOperation === "monday-saturday" && (
            <div className="space-y-4">
              <div>
                <h4 className="text-base font-medium mb-4">
                  Horário de Funcionamento (Segunda a sexta)
                </h4>
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                  <TimeSelect
                    timeType="open"
                    daysToCheck={WEEKDAYS}
                    defaultValue="08:00"
                    onValueChange={(value) => {
                      WEEKDAYS.forEach((day) => {
                        handleTimeChange(day, "open", value);
                      });
                    }}
                    disabled={fieldsDisabled}
                  />
                  <span className="text-sm text-muted-foreground">às</span>
                  <TimeSelect
                    timeType="close"
                    daysToCheck={WEEKDAYS}
                    defaultValue="18:00"
                    onValueChange={(value) => {
                      WEEKDAYS.forEach((day) => {
                        handleTimeChange(day, "close", value);
                      });
                    }}
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-base font-medium mb-4">
                  Horário de Funcionamento (Sábado)
                </h4>
                <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-md">
                  <TimeSelect
                    timeType="open"
                    daysToCheck={SATURDAY_ONLY}
                    defaultValue="08:00"
                    onValueChange={(value) =>
                      handleTimeChange("saturday", "open", value)
                    }
                    disabled={fieldsDisabled}
                  />
                  <span className="text-sm text-muted-foreground">às</span>
                  <TimeSelect
                    timeType="close"
                    daysToCheck={SATURDAY_ONLY}
                    defaultValue="14:00"
                    onValueChange={(value) =>
                      handleTimeChange("saturday", "close", value)
                    }
                    disabled={fieldsDisabled}
                  />
                </div>
              </div>
            </div>
          )}

          {watchedValues.daysOfOperation === "custom" && (
            <div>
              <h4 className="text-base font-medium mb-4">
                Horários personalizados por dia
              </h4>
              <div className="space-y-4">
                {daysOfWeek.map((day) => {
                  const dayHours =
                    watchedValues.operatingHours?.[day.key] || [];
                  const isEnabled = dayHours.length > 0;
                  const firstSlot = dayHours[0];

                  return (
                    <div key={day.key} className="flex items-center space-x-4">
                      <div className="flex items-center min-w-[140px] justify-between w-40">
                        <Label className="text-sm">{day.label}</Label>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked: boolean) =>
                            handleDayToggle(day.key, checked)
                          }
                          disabled={fieldsDisabled}
                        />
                      </div>

                      {isEnabled && firstSlot ? (
                        <div className="flex items-center space-x-2">
                          <Select
                            value={firstSlot.open}
                            onValueChange={(value) =>
                              handleTimeChange(day.key, "open", value)
                            }
                            disabled={fieldsDisabled}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, "0");
                                return (
                                  <SelectItem key={hour} value={`${hour}:00`}>
                                    {hour}:00
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <span className="text-sm text-muted-foreground">
                            às
                          </span>
                          <Select
                            value={firstSlot.close}
                            onValueChange={(value) =>
                              handleTimeChange(day.key, "close", value)
                            }
                            disabled={fieldsDisabled}
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Array.from({ length: 24 }, (_, i) => {
                                const hour = i.toString().padStart(2, "0");
                                return (
                                  <SelectItem key={hour} value={`${hour}:00`}>
                                    {hour}:00
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : !isEnabled ? (
                        <span className="text-sm text-muted-foreground">
                          Fechado
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className={`bg-white rounded-lg border p-6 ${fieldsDisabled ? "opacity-50" : ""}`}
      >
        <h3 className="text-lg font-semibold mb-4">Valor</h3>
        <div className="space-y-4">
          <div
            ref={
              errors.monthlyPrice && registerField
                ? (el) => registerField("monthlyPrice", el)
                : undefined
            }
          >
            <Label htmlFor="monthlyPrice">Preço mensal*</Label>
            <Input
              id="monthlyPrice"
              type="text"
              placeholder="Ex.: 1.800,00"
              value={watchedValues.monthlyPrice || ""}
              onChange={handlePriceChange}
              disabled={fieldsDisabled}
              onKeyDown={(e) => {
                if (e.key === "-") {
                  e.preventDefault();
                }
              }}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Mínimo de R$ 500
            </p>
            {(priceError || errors.monthlyPrice) && (
              <p className="text-sm text-destructive mt-1">
                {priceError || errors.monthlyPrice?.[0]}
              </p>
            )}

            {watchedValues.externalSpaceId &&
            neighborhoodFetched &&
            !hasNeighborhood ? null : (
              <PriceAnalysisAlert
                analysis={statisticalData?.analyses?.monthlyPrice}
                loading={isLoadingAnalysis}
                hasRequiredData={hasRequiredData}
                neighborhood={neighborhood}
                sizeM2={watchedValues.size}
                capacity={watchedValues.capacity}
                monthlyPrice={watchedValues.monthlyPrice}
                sampleSize={statisticalData?.sampleSize}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
